import { Wrench, ChevronDown } from "lucide-react";
import { MenuButton } from "~/components/assistant/MenuButton";
import { PopoverButton } from "~/components/assistant/PopoverButton";
import type { MCPServer } from "~/components/assistant/types";
import { Label } from "~/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Switch } from "~/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";

interface ToolSelectorProps {
  servers: MCPServer[];
  selectedServerIds: string[];
  onToggle: (serverId: string) => void;
  debugMode: boolean;
  onToggleDebug: () => void;
}

export function ToolSelector({
  servers,
  selectedServerIds,
  onToggle,
  debugMode,
  onToggleDebug,
}: ToolSelectorProps) {
  const showDevModeToggle = import.meta.env.VITE_CUSTOMER === "manifold";
  const isDisabled = servers.length === 0;
  const summaryLabel =
    selectedServerIds.length > 0
      ? `${selectedServerIds.length} tool${selectedServerIds.length !== 1 ? "s" : ""}`
      : "No tools";

  return (
    <Tooltip delayDuration={700}>
      <Popover>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <PopoverButton
              variant="ghost"
              disabled={isDisabled}
              className="disabled:opacity-50 disabled:cursor-not-allowed has-[>svg]:px-2"
              aria-label="Select Tools"
            >
              <Wrench className="size-4 text-gray-600" />
              <div className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-gray-700 text-[13px] text-left text-nowrap">
                <p className="block leading-[16px] whitespace-pre">{summaryLabel}</p>
              </div>
              <ChevronDown className="size-4 text-gray-400" />
            </PopoverButton>
          </PopoverTrigger>
        </TooltipTrigger>
        <PopoverContent className="w-56 p-2 max-h-[450px] overflow-y-auto" align="start">
          {showDevModeToggle && (
            <>
              <div className="flex items-center justify-between px-2 py-1">
                <Label htmlFor="debug-switch" className="text-[13px]">
                  Debug
                </Label>
                <Switch id="debug-switch" checked={debugMode} onCheckedChange={onToggleDebug} />
              </div>
              <div className="border-t border-gray-200 mx-2 my-2" />
            </>
          )}
          <div>
            {servers.map((server) => (
              <MenuButton
                key={server.id}
                onClick={() => onToggle(server.id)}
                label={server.name}
                ariaLabel="Select MCP servers"
                leftIcon={<Wrench className="size-4 text-gray-600" />}
                checkmark="right"
                isSelected={selectedServerIds.includes(server.id)}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <TooltipContent>
        <p>Select Data Tools</p>
      </TooltipContent>
    </Tooltip>
  );
}
